/** Qadamlar uchun umumiy qismlar: `.sbody`+`#ft` ramkasi, javob varag'i (demo `sheet`), `diffMark`, javob yozish. */
import { useEffect, useRef, type ReactNode } from 'react';
import type { Word } from '../../../data/types';
import { t } from '../../../i18n';
import { chime } from '../../../lib/sound';
import { haptic } from '../../../lib/telegram';
import { Ic } from '../../../ui/Icon';
import { mark, next, showFeedback, useLesson, type Feedback } from '../engine';

/** Javobni yozish: mark + haptic + pastki varaq. */
export function answer(w: Word, ok: boolean, opts: Omit<Feedback, 'ok' | 'w'> = {}) {
  mark(w, ok);
  if (!ok) haptic.bad(); // to'g'ri javobda vibratsiya yo'q
  showFeedback({ ok, w, ...opts });
}

/** Demo `sess(inner, foot)` ning qadam qismi: `.sbody` + `#ft` (varaq bo'lsa — varaq, `sheet` — qadamning o'z varag'i (match), aks holda qadam tugmalari). */
export function Frame({ children, foot, sheet }: { children: ReactNode; foot?: ReactNode; sheet?: ReactNode }) {
  const f = useLesson((s) => s.L?.feedback ?? null);
  return (
    <>
      <div className="sbody fade" id="sb">{children}</div>
      <div id="ft">{f ? <FeedbackSheet f={f} /> : sheet ? sheet : foot ? <div className="sfoot">{foot}</div> : null}</div>
    </>
  );
}

/** Demo `sheet(ok, w, opts)`. */
export function FeedbackSheet({ f }: { f: Feedback }) {
  const mode = useLesson((s) => s.L?.mode);
  const last = useLesson((s) => s.L?.last ?? 0);
  const btn = useRef<HTMLButtonElement>(null);
  useEffect(() => { if (f.ok) chime(); btn.current?.focus(); }, [f]);
  const { ok, w } = f;
  const defMsg = !ok && mode !== 'test'
    ? (mode === 'lesson' ? t('Bu soʻz shu bosqich oxirida yana chiqadi. Hamma soʻzlar toʻgʻri topilgach, keyingi bosqichga oʻtasiz.') : t('Bu soʻz oxirida yana chiqadi.'))
    : null;
  return (
    <div className={`sheet ${ok ? 'ok' : 'bad'}`}>
      <div className="hd">
        <i><Ic n={ok ? 'check' : 'x'} style={{ width: 22, height: 22, strokeWidth: 2.6 }} /></i>
        <b>{ok ? t('Barakalla!') : (f.head || t('Toʻgʻri javob:'))}</b>
        {ok && last > 0 && <span className="xp">+{last} XP</span>}
      </div>
      {!ok && !f.ans
        ? <div className="ans" style={{ fontSize: 22, lineHeight: '30px' }}>{w.w} — {w.uz}</div>
        : f.ans ? <div className="ans">{f.ans}</div> : null}
      {f.msg ? <p className="msg">{f.msg}</p> : defMsg ? <p className="msg">{defMsg}</p> : null}
      <button className="btn" id="cont" ref={btn} onClick={next}>{ok ? t('Davom etish') : t('Tushunarli')}</button>
    </div>
  );
}

/** Demo `diffMark(ans, correct)`: foydalanuvchi o'tkazib yuborgan yoki xato yozgan harflar tagiga chiziladi. */
export function DiffMark({ ans, correct }: { ans: string; correct: string }) {
  const a = ans.toLowerCase(), c = correct; const out: ReactNode[] = []; let j = 0;
  for (let i = 0; i < c.length; i++) {
    const ch = c[i]!;
    if (j < a.length && a[j] === ch.toLowerCase()) { out.push(ch); j++; }
    else if (j + 1 < a.length && a[j + 1] === ch.toLowerCase()) { j++; out.push(<u key={i}>{ch}</u>); j++; }
    else out.push(<u key={i}>{ch}</u>);
  }
  return <>{out}</>;
}

/** Qadam sarlavhasi: «Yana bir bor: » prefiksi bilan (demo `s.retry`). */
export function Prompt({ retry, text }: { retry?: boolean; text: string }) {
  return <p className="t15b" style={{ color: 'var(--muted)', margin: 0 }}>{retry ? t(`Yana bir bor: ${text}`) : t(text)}</p>;
}

/** Variantlar ro'yxati (rec/recUz/listen/gap): tanlangach hammasi o'chadi, to'g'risi `.ok`, xatosi `.bad`, qolgani `.dim`. */
export function Options({ opts, label, answerId, picked, onPick }: { opts: Word[]; label: (o: Word) => string; answerId: number; picked: number | null; onPick: (i: number) => void }) {
  return (
    <div className="opts">
      {opts.map((o, i) => {
        const isC = o.id === answerId, done = picked !== null;
        const cls = ['opt', done && isC ? 'ok' : '', done && !isC && i === picked ? 'bad' : '', done && !isC && i !== picked ? 'dim' : ''].filter(Boolean).join(' ');
        return (
          <button key={o.id} className={cls} disabled={done} onClick={() => onPick(i)}>
            <span>{label(o)}</span>{done && isC && <Ic n="check" />}
          </button>
        );
      })}
    </div>
  );
}
